import {addNamespace} from '#/notes/store/helpers'

export const mutationTypes = {
  /**
   * sets the Notes array into the state
   * state.noteRecords is the only Notes list in our global state
   * It represents the current Notes data given the current state of the system
   * @param {Array} notes array of Note objects
   */
  SET_NOTES: 'setNotes',
  /**
   * sets the currentNoteRecord field to the given note
   * state.currentNoteRecord is the note being detailed
   * @param {Object} note the note
   */
  SET_CURRENT_NOTE: 'setCurrentNote',
  /**
   * resets the currentNoteRecord field to the initial value (null)
   * state.currentNoteRecord is the note being detailed
   */
  RESET_CURRENT_NOTE: 'resetCurrentNote',
  /**
   * sets the currentNoteHistory field to the given note history
   * state.currentNoteHistory is the history of the current note being detailed
   * @param {Array} array of Note history objects
   */
  SET_CURRENT_NOTE_HISTORY: 'setCurrentNoteHistory',
  /**
   * removes the note containing the given id
   * @param {String} id the id for the note to be removed
   */
  REMOVE_NOTE_BY_ID: 'remoteNoteById',
  /**
   * concatenates the given notes into the notes array
   * @param {Array} notes array of Note objects
   */
  APPEND_NOTES: 'appendNotes',
  /**
   * Updates an existing Note in the Notes array
   * the note.id is used to find the note inside the Notes array
   * @param {Object} note the note object to be updated
   */
  UPDATE_NOTE: 'setNote',
  /**
   * sets the current search string into the state
   * state.currentSearch is used as a payload property when fetching for Notes
   * it represents the search term being applied to the Notes list
   * @param {String} search the search term
   */
  SET_SEARCH: 'setCurrentSearch',
  /**
   * resets the state to its initial state
   * this is mostly used as a cleanup method when leaving a certain context
   * @param {Object} initialState the state to reset to
   */
  RESET: 'reset',
  /**
   * sets the consumer filters
   * state.consumerFilters is used to hold the consumer filters being applied to the Notes list
   * this value is supplied by notepad wrapper of other BriteCore products.
   * consumer filters have higher precedence than current filters
   */
  SET_CONSUMER_FILTERS: 'setConsumerFilters',
  /**
   * adds new filters to the user defined filters, replaces the duplicates
   * state.userFilters is used to hold the user defined filters being applied to the Notes list
   * @param {Object} filters the new filters to be concatenated into the current ones
   */
  UPDATE_USER_FILTERS: 'updateUserFilters',
  /**
   * removes the given filters from the user defined filters
   * state.userFilters is used to hold the user defined filters being applied to the Notes list
   * @param {Object} filters the filters to be removed from the current ones
   */
  REMOVE_USER_FILTERS: 'removeUserFilters',
  /**
   * resets the user defined filters to an empty value
   * state.userFilters is used to hold the user defined filters being applied to the Notes list
   */
  RESET_USER_FILTERS: 'resetUserFilters',
  /**
   * updates the pagination object to the given one
   * state.currentPagination consist of 3 properties - total, page and pageSize
   * this is used to control the pagination aspect of the Notes list
   * @param {Object} pagination the values of total, page and/or pageSize to be updated
   */
  UPDATE_PAGINATION: 'updatePagination',
  /**
   * this flags indicates whether the current list should be paginated or not
   * @param {Boolean} flag a boolean value to toggle pagination
   */
  TOGGLE_PAGINATED_FETCHING: 'togglePaginatedFetching',
  /**
   * this flags indicates whether we are fetching data or not, used for setting loading/disabled states
   * @param {Boolean} flag a boolean value to toggle isFetching
   */
  TOGGLE_IS_FETCHING: 'toggleIsFetching',
  /**
   * Sets the list reading mode
   * @param {String} readingMode one of the reading modes defined at the definitions module under the NOTES_READING_MODE constant
   */
  SET_READING_MODE: 'setReadingMode',
  /**
   * Sets the notes visualization mode
   * @param {String} mode one of the visualization modes defined at the definitions module under the NOTES_MODES constant
   */
  SET_CURRENT_MODE: 'setCurrentMode',
  /**
   * Sets the environment GEN2/GEN3
   * @param {String} environment one of the environments defined at the definitions module under the ENVIRONMENTS constant
   */
  SET_ENVIRONMENT: 'setEnvironment',
  /**
   * Sets the user defined filters' metadata
   * state.userFiltersMetadata holds information about the user defined filters such as label and value in human-readable format
   * @param {String} metadata the extracted data from the filter form
   */
  SET_USER_FILTERS_METADATA: 'setUserFiltersMetadata',
  /**
   * Resets the user defined filters' metadata
   * state.userFiltersMetadata holds information about the user defined filters such as label and value in human-readable format
   */
  RESET_USER_FILTERS_METADATA: 'resetUserFiltersMetadata',
  /**
   * sets the Note tags array into the state
   * state.noteTagRecords is the only Note tags list in our global state
   * It represents the current Note tags data given the current state of the system
   * @param {Array} noteTags array of Note tag objects
   */
  SET_NOTE_TAGS: 'setNoteTags',
  /**
   * sets the Note types array into the state
   * state.noteTypeRecords is the only Note types list in our global state
   * It represents the current Note types data given the current state of the system
   * @param {Array} noteTypes array of Note type objects
   */
  SET_NOTE_TYPES: 'setNoteTypes',
  /**
   * sets the domains array into the state
   * state.domains is the only domain list in our global state
   * It represents the current domain data given the current state of the system
   * @param {Array} domains array of Note domain objects
   */
  SET_DOMAINS: 'setDomains',
  /**
   * this flags indicates whether the current Note data has been changed and not saved yet
   * @param {Boolean} flag a boolean value to setNoteIsDirty to true if user changed the note
   */
  SET_NOTE_IS_DIRTY: 'setNoteIsDirty'
}

export default addNamespace('notes/', mutationTypes)
